import { useEffect } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Phone, CheckCircle2 } from "lucide-react";
import Navbar from "../components/sections/Navbar";
import Footer from "../components/sections/Footer";
import FloatingCTA from "../components/FloatingCTA";
import { getService, SERVICES } from "../data/services";

export default function ServicePage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "");
  const svc = getService(slug);

  useEffect(() => {
    if (!svc) return;
    document.title = `${svc.title} — Master-Bathrooms`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", svc.intro);
  }, [svc]);

  if (!svc) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <main className="pt-28 md:pt-32">
        <section className="max-w-6xl mx-auto px-5 md:px-8 py-12 md:py-16">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#1E5BC6] font-semibold mb-8 hover:underline"
          >
            <ArrowLeft size={14} /> Terug naar home
          </Link>
          <span className="tag-overline">{svc.tag}</span>
          <h1 className="mt-4 text-5xl md:text-7xl text-[#0A0A0A] uppercase tracking-tight leading-[1.02] max-w-4xl">
            {svc.h1}
          </h1>
          <p className="mt-8 text-lg md:text-xl text-[#5A5A5A] max-w-3xl leading-relaxed">
            {svc.intro}
          </p>
        </section>

        <section className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="aspect-[16/9] overflow-hidden">
            <img
              src={svc.image}
              alt={svc.title}
              className="w-full h-full object-cover"
            />
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-5 md:px-8 py-20 grid md:grid-cols-3 gap-8">
          {svc.sections.map((s, i) => (
            <div key={i} className="border-l-2 border-[#1E5BC6] pl-6">
              <CheckCircle2 className="text-[#1E5BC6] mb-4" size={22} />
              <h2 className="text-xl md:text-2xl text-[#0A0A0A] uppercase tracking-tight font-extrabold">
                {s.h}
              </h2>
              <p className="mt-3 text-[#5A5A5A] leading-relaxed">{s.p}</p>
            </div>
          ))}
        </section>

        <section className="bg-[#0A0A0A] text-white py-20 md:py-24">
          <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
            <h2 className="text-4xl md:text-5xl uppercase tracking-tight">
              Klaar om te starten?
            </h2>
            <p className="mt-6 text-lg text-[#C7C7C7] max-w-xl mx-auto">
              Plan een gratis, vrijblijvend adviesgesprek bij u thuis of op locatie.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/#contact"
                className="btn-primary-mb px-8 py-4 text-xs inline-flex items-center justify-center gap-2"
              >
                Plan adviesgesprek <ArrowRight size={16} />
              </Link>
              <a
                href="tel:+31687445044"
                className="btn-outline-mb bg-transparent border-white text-white hover:bg-white hover:text-black px-8 py-4 text-xs inline-flex items-center justify-center gap-2"
              >
                <Phone size={16} /> 06 87445044
              </a>
            </div>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-5 md:px-8 py-16">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#5A5A5A] font-bold mb-6">
            Andere diensten
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {SERVICES.filter((s) => s.slug !== svc.slug).map((s) => (
              <Link
                key={s.slug}
                to={`/${s.slug}`}
                className="border border-[#E5E7EB] p-5 hover:border-[#1E5BC6] transition-colors group"
              >
                <div className="text-sm font-bold text-[#0A0A0A] uppercase group-hover:text-[#1E5BC6]">
                  {s.title}
                </div>
                <ArrowRight size={16} className="mt-3 text-[#1E5BC6]" />
              </Link>
            ))}
          </div>
        </section>
      </main>
      <Footer />
      <FloatingCTA />
    </div>
  );
}
